import { FC, MouseEvent, SyntheticEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import TimeHelper from "../../../../helpers/TimeHelper";
import { useFlashMessage } from "../../../../hooks/useFlashMessage";
import KarteInterface from "../../../../interfaces/KarteInterface";
import Icon from "../../../atoms/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "../../../general/Tooltip/Tooltip";
import KarteShareButton from "../KarteShareButton";
import KarteDropdown from "./KarteDropdown";
import styles from "./styles.scss";
import { useDeleteKarte } from "./useDeleteKarte";
import { useDeleteKartePin } from "./useDeleteKartePin";
import { usePostKartePin } from "./usePostKartePin";

interface Props {
  sectionId: string;
  karte: KarteInterface;
  canPin?: boolean;
}

const KarteItemHeader: FC<Props> = ({ sectionId, karte, canPin }) => {
  const { mutate: deleteKarte } = useDeleteKarte({
    sectionId,
  });
  const { mutate: postKartePin } = usePostKartePin();
  const { mutate: deleteKartePin } = useDeleteKartePin();

  const navigate = useNavigate();
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  // 現在のURLによってカルテ編集後に戻る先を校舎カルテ一覧と生徒カルテ一覧にわける
  const editSearch = new URLSearchParams({
    backto: window.location.pathname.startsWith("/students")
      ? "students"
      : "sections",
  });
  const editLinkTo = {
    pathname: `/students/${karte.student.id}/kartes/${karte.id}/edit`,
    search: editSearch.toString(),
  };

  const handleDeleteClick = (
    e: MouseEvent<Element, MouseEvent> | SyntheticEvent,
  ): void => {
    e.preventDefault();
    if (window.confirm("本当に削除してよろしいですか？")) {
      deleteKarte(
        { studentId: karte.student.id, karteId: karte.id },
        {
          onSuccess: () => {
            showSuccessMessage("カルテを削除しました");
            navigate(`/students/${karte.student.id}/kartes`);
          },
          onError: () => {
            showErrorMessage("カルテの削除に失敗しました");
          },
        },
      );
    }
  };

  const handlePostKartePin = (): void => {
    if (window.confirm("このカルテをトップに固定しますか？")) {
      postKartePin(
        { studentId: karte.student.id, karteId: karte.id },
        {
          onError: () => {
            showErrorMessage("トップに固定を失敗しました");
          },
        },
      );
    }
  };

  const handleDeleteKartePin = (): void => {
    if (window.confirm("このカルテをトップから外しますか？")) {
      deleteKartePin(
        { studentId: karte.student.id, karteId: karte.id },
        {
          onError: () => {
            showErrorMessage("トップに固定を解除に失敗しました");
          },
        },
      );
    }
  };

  const handleClickPin = (): void => {
    if (karte.pinned) {
      handleDeleteKartePin();
    } else {
      handlePostKartePin();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.left}>
        <Icon name="icon-navi-avatar" className={styles.icon} />
        <Link
          to={`/students/${karte.student.id}/kartes`}
          className={styles.name}
        >
          {karte.student.fullName}
        </Link>
        <Link
          to={`/students/${karte.student.id}/kartes/${karte.id}`}
          className={styles.createdAt}
        >
          <time>{TimeHelper.japaneseFormatFullDateTime(karte.createdAt)}</time>
        </Link>
        {canPin && karte.pinned && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  data-for={`KarteItemHeader-${karte.id}-tooltip`}
                  data-tip={true}
                  className={`${styles.pin} ml-[1.4rem] leading-[0]`} // ./styles.scssでmargin-left: 1.4remを当てて左の要素との間隔を取っていたため
                >
                  <Icon name="icon-pin" onClick={handleClickPin} />
                </div>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>
                  <p>トップに固定を解除</p>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className={styles.right} aria-label="カルテ編集メニュー">
        <Link to={editLinkTo} className={styles.editLink}>
          <Icon name="icon-edit" />
        </Link>
        <span className={styles.staffName}>{karte.staffName}</span>
        <KarteShareButton karte={karte} />
        <KarteDropdown
          studentId={karte.student.id}
          karteId={karte.id}
          canPin={canPin}
          isPinned={karte.pinned}
          onPinClick={handleClickPin}
          onDeleteClick={handleDeleteClick}
        />
      </div>
    </div>
  );
};

export default KarteItemHeader;
